import { observer } from 'mobx-react';
import { T } from '../../../../Translations';
import CloseIcon from '../../../Cutesies/CloseIcon';
import { importParticipantsV2Store } from '../Store';

interface ParticipantChipsProps {
  onUploadClick: () => void;
}

function ParticipantChips({ onUploadClick }: ParticipantChipsProps) {
  const store = importParticipantsV2Store;

  // Get participants from various states where they exist
  const getParticipants = () => {
    const state = store.state;
    switch (state.kind) {
      case 'file-processed':
      case 'submitting':
      case 'submit-failed':
      case 'submit-succeeded':
        return state.participants;
      case 'hellocsv-open':
        return state.participants.getOrElseValue([]);
      default:
        return [];
    }
  };

  const participants = getParticipants();

  const handleRemoveParticipant = (index: number) => {
    store.removeParticipantAtIndex(index);
  };

  const getDisplayText = (participant: { email: string }) => {
    return participant.email;
  };

  return (
    <div className="mb-6">
      {/* Chips container with Upload CSV button */}
      <div className="flex gap-4">
        <div
          className={`flex-1 min-h-[80px] p-3 border border-gray-300 rounded-md bg-white${
            participants.length === 0
              ? ' cursor-pointer hover:border-teal-600 hover:shadow-md transition-all'
              : ''
          }`}
          onClick={participants.length === 0 ? onUploadClick : undefined}
        >
          {participants.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {participants.map((participant, index) => (
                <div
                  key={`${participant.email}-${index}`}
                  className="inline-flex items-center px-3 py-1.5 rounded-md border border-teal-600 bg-white text-sm text-teal-700"
                >
                  <span className="mr-2">{getDisplayText(participant)}</span>
                  <button
                    onClick={() => handleRemoveParticipant(index)}
                    className="items-center justify-center w-4 h-4 bg-teal-600 hover:bg-teal-700 rounded focus:outline-none [&_svg]:fill-white [&_svg]:stroke-white"
                    aria-label={`Remove ${participant.email}`}
                    data-intercom-target="import-participants-v2-remove-participant"
                  >
                    <CloseIcon variant="small" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="flex items-center justify-center min-h-[56px] text-gray-400 text-sm"
              data-intercom-target="import-participants-v2-empty-state-upload"
            >
              <T kind="No participants added yet" />
            </div>
          )}
        </div>

        <button
          onClick={onUploadClick}
          className="px-4 py-2 h-fit border border-teal-600 text-teal-600 rounded-md hover:bg-teal-50 font-medium whitespace-nowrap bg-white text-sm"
          data-intercom-target="import-participants-v2-upload-csv"
        >
          <T kind="Upload CSV" />
        </button>
      </div>

      {/* Participants count */}
      {participants.length > 0 && (
        <div className="mt-2">
          <span className="text-teal-600 text-sm font-medium">
            <T kind="Participants to add" /> ({participants.length})
          </span>
        </div>
      )}
    </div>
  );
}

export default observer(ParticipantChips);
